// @LTG-ID: data/journals/how-much-is-my-business-worth
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: /app/docs/data-model/journal_content_v1.md, /app/docs/09_CHANGELOG.md#CHG-0107
// @CHANGED: 2026-07-06 (CHG-0107 · Journals content port from batch MD)
// @OWNER: web
//
// Article 1 of 20. Source: batch MD at /app/_ltg_analysis/content_batches/.
// Byline: LTG Team (per CHG-0107 orchestrator Decision 2 / batch preamble directive).

export const BRIEF = {
    SLUG: "how-much-is-my-business-worth",
    FRONTMATTER: {
        number: "38",
        section: "Valuation",
        sectionHref: "/journals#core-valuation",
        sectionAnchor: "core-valuation",
        title: "How Much Is My Business Worth? The Owner's Complete Guide to Business Valuation",
        deck: "Most small and mid-sized businesses are worth a multiple of their earnings, typically applied to seller's discretionary earnings or EBITDA.",
        author: "LTG Team",
        authorRole: "",
        authorInitials: "LT",
        publishedISO: "2026-04-28",
        publishedHuman: "April 28, 2026",
        lastReviewed: "2026-08-27",
        readMinutes: 5,
        wordCount: 838,
        timeRequired: "PT5M",
        keywords: "how much is my business worth, business valuation for small business, what is my company worth, business worth calculator",
        articleSection: "Valuation",
        articleBodyPreview: "Most small and mid sized businesses are worth a multiple of their earnings, typically applied to seller's discretionary earnings or EBITDA. The multiple depends on industry, size, growth, owner dependence, customer concentration, and the quality of financial records. A market based estimate is a starting point, not a formal appraisal. Every owner eventually asks the same question: what is this business actually worth? The honest answer is that value is not a single number printed somewhere waiti",
        complianceNote: "This article is educational and does not constitute a valuation engagement, appraisal, or financial, legal, or tax advice. Consult qualified professionals about your specific situation.",
        needsDisclaimerFooter: true,
        internalLinks: [
        "what-is-ebitda",
        "sde-vs-ebitda-guide",
        "what-is-a-valuation-multiple",
        "what-percentage-of-businesses-sell",
        "owner-dependence-business-value",
        "asset-sale-vs-stock-sale"
    ],
        ctaId: "journal-article-primary",
        ctaLabel: "Get Your Valuation",
        pageTitle: "How Much Is My Business Worth? Owner's Valuation Guide · LTG",
        canonical: "https://letstalkgrowth.com/journals/how-much-is-my-business-worth",
        ogImage: "/journals/og/how-much-is-my-business-worth.jpg",
    },
    BODY_MARKDOWN: `Most small and mid-sized businesses are worth a multiple of their earnings, typically applied to seller's discretionary earnings or EBITDA. The multiple depends on industry, size, growth, owner dependence, customer concentration, and the quality of financial records. A market-based estimate is a starting point, not a formal appraisal.

Every owner eventually asks the same question: what is this business actually worth? The honest answer is that value is not a single number printed somewhere waiting to be discovered. It is a range, and where your business lands inside that range is driven by factors you can understand and, in most cases, improve.

## The basic math buyers use

For most privately held businesses, buyers start with a simple structure: a measure of earnings multiplied by a market multiple.

The earnings measure is usually one of two things. Smaller, owner-operated businesses are typically measured on seller's discretionary earnings, or SDE, which is the profit available to a single full-time owner-operator. Larger businesses with management teams are typically measured on EBITDA, which is earnings before interest, taxes, depreciation, and amortization. The difference matters, and choosing the wrong one is one of the most common reasons owners overestimate or underestimate value. We cover the distinction in depth in our SDE vs EBITDA guide.

The multiple is where the market speaks. According to BizBuySell Insight Reports, which track closed small business transactions across the United States, Main Street businesses commonly change hands at low single-digit multiples of SDE, with the exact figure varying by industry, size, and year. Larger companies measured on EBITDA generally command higher multiples than smaller ones, because buyers pay for durability, management depth, and reduced risk.

## Why two businesses with identical profits sell for different prices

Imagine two plumbing companies, each producing the same annual profit. One depends entirely on the owner: he sells the work, manages the crews, and holds every customer relationship. The other has an operations manager, documented processes, service agreements that renew annually, and no customer representing more than a small share of revenue.

A buyer will pay meaningfully more for the second business. Not because the profit is different, but because the risk is different. This is the single most important idea in valuation for owners: buyers price risk, and everything that lowers risk raises value.

The factors buyers weigh most heavily include:

- **Owner dependence.** Can the business run without you? If not, a buyer is partly buying a job, not a company.
- **Revenue quality.** Recurring and contracted revenue is worth more than one-time project revenue.
- **Customer concentration.** Heavy reliance on one or two customers is a discount, sometimes a deal-breaker.
- **Financial records.** Clean, accountant-prepared statements build confidence. Messy books create doubt, and doubt is expensive.
- **Team depth.** A capable second layer of management signals that value transfers with the sale.
- **Growth trend.** Buyers pay for the future, and a rising three-year trend supports a stronger price than a flat or declining one.

## The three formal approaches to value

Professional appraisers organize valuation into three approaches, and it helps owners to know the vocabulary:

1. **The market approach** compares your business to actual sales of similar businesses. For most small companies, this is the most intuitive and most used.
2. **The income approach** values the business on its expected future cash flows, discounted for risk. It dominates in larger and more formal contexts.
3. **The asset approach** values the business on the fair value of its assets minus liabilities. It typically sets a floor and matters most for asset-heavy or underperforming businesses.

A credible estimate usually leans on the market approach with a sanity check from the others.

## Estimate vs appraisal: know which one you need

An indicative, market-based estimate is appropriate for planning: understanding where you stand, setting goals, and deciding when to start an exit process. A formal valuation engagement performed by a credentialed appraiser is a different product, required in contexts like litigation, divorce, estate and gift tax filings, and some buy-sell agreement triggers. Knowing which one your situation calls for saves both money and confusion.

## What owners get wrong most often

Three misconceptions come up constantly. First, revenue is not value: a business with high revenue and thin profits is usually worth less than a smaller business with strong margins. Second, what you need for retirement has no bearing on what a buyer will pay: the market does not price your plans. Third, rules of thumb you hear at industry events are averages across wildly different businesses: your business is not average, in one direction or the other.

## Where to start

Start by understanding your true earnings, normalized for owner compensation and one-time items, and then look honestly at the risk factors above. The gap between what your business is worth today and what it could be worth is usually a list of specific, fixable items. That list is the real output of a valuation exercise, and it is worth far more than the number itself.`,
    FAQ: [
        { question: "Is my business worth a multiple of revenue or a multiple of profit?", answer: "For the large majority of private businesses, value is based on a multiple of earnings, not revenue. Revenue multiples appear mainly in a few sectors with unusual economics. Two businesses with the same revenue can have very different values because their profitability and risk differ." },
        { question: "Can I use an online calculator to value my business?", answer: "An online estimate based on real financial inputs can give you a useful starting range for planning. It is not a formal appraisal, and it cannot be used for legal or tax purposes. Treat it as a compass, not a survey." },
        { question: "Does the value include my real estate?", answer: "Usually not. Business value and real estate value are typically calculated separately, with the business paying market rent in the analysis, even when the same owner holds both." },
        { question: "How often should I re-estimate my business's value?", answer: "Annually is a sensible rhythm for most owners, and any time something material changes: a large customer win or loss, a key hire or departure, or a significant shift in profitability." }
    ],
    SOURCES: [

    ],
    END_CTA: {
        label: "READY TO KNOW YOUR NUMBER?",
        headlineLead: "What is your business ",
        headlineEm: "actually",
        headlineTrail: " worth?",
        body: "Indicative valuation signed by Sara. $995 flat.",
        secondaryHref: "/journals",
        secondaryLabel: "More from the Journal →",
        microLine: "· 7-DAY DELIVERY · KGOB METHODOLOGY, NC CPA #30420 ·",
    },
    READ_NEXT: [
        {
            tag: "Valuation",
            title: "What Is EBITDA? A Plain-English Guide for Business Owners",
            meta: "3 min · Brief 40",
            to: "/journals/what-is-ebitda",
        },
        {
            tag: "Valuation",
            title: "SDE vs EBITDA: Which One Applies to Your Business?",
            meta: "3 min · Brief 41",
            to: "/journals/sde-vs-ebitda-guide",
        },
        {
            tag: "Valuation",
            title: "What Is a Valuation Multiple and Why Does Everyone Talk About Them?",
            meta: "3 min · Brief 42",
            to: "/journals/what-is-a-valuation-multiple",
        },
    ],
};

export default BRIEF;
